import { RefreshCw } from "lucide-react";
import React, { useState, useEffect } from "react";
import {
  Button,
  Drawer,
  Space,
  Modal,
  Text,
  Container,
  Group,
  ActionIcon,
} from "@mantine/core";
import PermissionProtectedComponent from "../../../common/uac/PermissionProtectedComponent";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import { PERMISSION_ADD_PAYSLIP } from "../../../utils/UserAccessControlUtils";
import PayslipUploadForm from "../../payslips/components/PayslipUploadForm";
import PayslipUploadHistoryTable from "../../payslips/components/PayslipUploadHistoryTable";
import DocumentProcessingJob from "../../payslips/services/model/DocumentProcessingJob";
import DocumentImporterConfiguration from "../../payslips/services/model/DocumentImporterConfiguration";
import { RosterJourneyConfiguration } from "../RosterJourneyConfiguration";
import NotFound from "../../../common/pages/NotFound";
import { AccessPermissionContext } from "../../../providers/AccessPermissionsProvider";
import FeatureFlagProtectedComponent from "../../../common/feature-flagging/FeatureFlagProtectedComponent";
import FeatureFlags from "../../../common/feature-flagging/FeatureFlags";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";
import { HELP_CENTER_URL } from "../../help-center/constants";

const RosterDirectoryPage: React.FC = () => {
  const documentImporterConfiguration: DocumentImporterConfiguration =
    RosterJourneyConfiguration;

  const [opened, setOpened] = useState(false);
  const [selectionDocumentUpload, setSelectionUpload]: [
    DocumentProcessingJob[],
    React.Dispatch<React.SetStateAction<DocumentProcessingJob[]>>
  ] = useState<DocumentProcessingJob[]>([]); // Explicitly typed empty array

  const [exitingUpload, setExitingUpload] = useState(false);
  const [uploadingClicked, setUploadingClicked] = useState(false);

  const { hasPermissions } = React.useContext(AccessPermissionContext);
  const { trackDocumentsEvent } = React.useContext(AnalyticsContext);

  useEffect(() => {
    trackDocumentsEvent({
      event: `${documentImporterConfiguration.document_name_plural}_Dashboard`,
      description: `User navigates to the ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
      event_type: "PageView",
      type: documentImporterConfiguration.document_name_plural,
      job: null,
    });
  }, [trackDocumentsEvent]);

  if (!hasPermissions) {
    return <NotFound />;
  }

  return (
    <PageContainer>
      <Drawer
        opened={opened}
        size="xl"
        onClose={() => {
          if (uploadingClicked) {
            setExitingUpload(true);
          } else {
            setOpened(false);
          }
        }}
        title={`Upload ${documentImporterConfiguration.document_name_plural}`}
        overlayOpacity={0.1}
        overlayBlur={3}
        position="right"
      >
        <PayslipUploadForm
          documentImporterConfiguration={documentImporterConfiguration}
          onSuccess={() => {
            setOpened(false);
            setExitingUpload(false);
            setUploadingClicked(false);
          }}
          setExitingUpload={setExitingUpload}
          setUploadingClicked={setUploadingClicked}
        />
      </Drawer>
      <Modal
        size="40%"
        title={`Do you want to cancel this ${documentImporterConfiguration.document_name_lower_case} upload?`}
        opened={exitingUpload}
        centered
        onClose={() => setExitingUpload(false)}
        closeOnClickOutside
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <Container>
          <Space h="xl" />
          <Text size="lg" align="center">
            Closing this window will cancel your current upload
          </Text>
          <Space h="xl" />
          <Group spacing="xl" position="center">
            <Button
              color="red"
              style={{ backgroundColor: "red" }}
              onClick={() => {
                setOpened(false);
                setExitingUpload(false);
              }}
            >
              Cancel Upload
            </Button>
            <Button
              onClick={() => {
                setOpened(true);
                setExitingUpload(false);
              }}
            >
              Continue Upload
            </Button>
          </Group>
          <Space h="xl" />
        </Container>
      </Modal>

      <PageHeading
        title={`${documentImporterConfiguration.document_name_plural}`}
      >
        <PermissionProtectedComponent
          requiredViewPermissions={[PERMISSION_ADD_PAYSLIP]}
        >
          <ActionIcon
            id="payslips-refresh-btn"
            variant="outline"
            className="inline-flex"
            aria-label="Refresh"
            sx={{
              border: "1px solid var(--grey-grey-50)",
            }}
            size={34}
            onClick={() => {
              trackDocumentsEvent({
                event: "Refresh_Icon",
                description: `User clicked 'refresh' icon on the ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
                event_type: "CTA:Click",
                type: documentImporterConfiguration.document_name_plural,
                job: null,
              });
              window.location.reload();
            }}
          >
            <RefreshCw size={18} />
          </ActionIcon>

          <Button
            id="roster-timesheet-upload-btn"
            type="button"
            variant="filled"
            onClick={() => {
              trackDocumentsEvent({
                event: `Upload_${documentImporterConfiguration.document_name_plural}_CTA_Header`,
                description: `User clicked 'Upload ${documentImporterConfiguration.document_name_plural}' button on the ${documentImporterConfiguration.document_name_plural_lower_case} dashboard header`,
                event_type: "CTA:Click",
                type: documentImporterConfiguration.document_name_plural,
                job: null,
              });
              setOpened(true);
            }}
            className="inline-flex"
            color="jem-theme"
          >
            Upload {documentImporterConfiguration.document_name_plural}
          </Button>
          <FeatureFlagProtectedComponent featureName={FeatureFlags.HELP_CENTER}>
            <Button
              id="help-center-contact-us-btn"
              type="button"
              variant="outline"
              p="0 20px"
              onClick={() => {
                trackDocumentsEvent({
                  event: `Need_Assistance_CTA`,
                  description: `User clicked 'Need Assistance' button on the ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
                  event_type: "CTA:Click",
                  type: documentImporterConfiguration.document_name_plural,
                  job: null,
                });
                window.open(HELP_CENTER_URL, "_blank");
              }}
              sx={{
                "&:active": {
                  backgroundColor: "rgba(237, 119, 135, 1)",
                },
              }}
              className="inline-flex secondary-action"
              color="grey"
            >
              Need Assistance?
            </Button>
          </FeatureFlagProtectedComponent>
        </PermissionProtectedComponent>
      </PageHeading>
      <Space h="xl" />
      <PayslipUploadHistoryTable
        documentImporterConfiguration={documentImporterConfiguration}
        onSelection={setSelectionUpload}
        selection={selectionDocumentUpload}
      />
    </PageContainer>
  );
};

export default RosterDirectoryPage;
